import fs from "node:fs";
import path from "node:path";
import { configDir } from "../auth/config";

/**
 * Local invocation audit log for debugging: every CLI run appends one JSONL
 * record to `<config dir>/logs/audit-YYYY-MM-DD.jsonl` when the process
 * exits, capturing the (redacted) argv, exit code, and duration. Files older
 * than the retention window (default 7 days) are pruned on startup. Logging
 * never blocks or fails a command — all filesystem errors are swallowed.
 *
 * Opt out with `WHOP_CLI_AUDIT_LOG=0`; tune retention with
 * `WHOP_CLI_AUDIT_RETENTION_DAYS`.
 */

export const DEFAULT_RETENTION_DAYS = 7;

const SENSITIVE_FLAG =
	/(api[-_]?key|tokens?|secrets?|passwords?|credentials?|founders?|business[-_]?(address|phone)|ssn|date[-_]?of[-_]?birth)$/i;
const SECRET_VALUE = /whop_[A-Za-z0-9_-]{8,}/g;
const LOG_FILE = /^audit-(\d{4}-\d{2}-\d{2})\.jsonl$/;

export interface AuditRecord {
	ts: string;
	argv: string[];
	exitCode: number;
	durationMs: number;
	version: string;
}

export function auditLogDir(env: NodeJS.ProcessEnv = process.env): string {
	return path.join(configDir(env), "logs");
}

export function auditLogDisabled(
	env: NodeJS.ProcessEnv = process.env,
): boolean {
	const value = env.WHOP_CLI_AUDIT_LOG?.trim().toLowerCase();
	return value === "0" || value === "false" || value === "off";
}

export function auditRetentionDays(
	env: NodeJS.ProcessEnv = process.env,
): number {
	const parsed = Number.parseInt(
		env.WHOP_CLI_AUDIT_RETENTION_DAYS?.trim() ?? "",
		10,
	);
	if (!Number.isFinite(parsed) || parsed < 1) return DEFAULT_RETENTION_DAYS;
	return parsed;
}

export function redactArgv(argv: string[]): string[] {
	const redacted: string[] = [];
	let redactNext = false;
	for (const arg of argv) {
		if (redactNext) {
			redacted.push("[redacted]");
			redactNext = false;
			continue;
		}
		if (arg.startsWith("--")) {
			const eq = arg.indexOf("=");
			const flag = eq === -1 ? arg : arg.slice(0, eq);
			if (SENSITIVE_FLAG.test(flag)) {
				if (eq === -1) {
					redactNext = true;
					redacted.push(arg);
				} else {
					redacted.push(`${flag}=[redacted]`);
				}
				continue;
			}
		}
		redacted.push(arg.replace(SECRET_VALUE, "[redacted]"));
	}
	return redacted;
}

export function pruneAuditLogs(
	env: NodeJS.ProcessEnv = process.env,
	now: Date = new Date(),
): void {
	const dir = auditLogDir(env);
	let entries: string[];
	try {
		entries = fs.readdirSync(dir);
	} catch {
		return;
	}
	const midnightUtc = Date.UTC(
		now.getUTCFullYear(),
		now.getUTCMonth(),
		now.getUTCDate(),
	);
	const cutoff = midnightUtc - auditRetentionDays(env) * 24 * 60 * 60 * 1000;
	for (const entry of entries) {
		const match = LOG_FILE.exec(entry);
		if (!match) continue;
		if (new Date(`${match[1]}T00:00:00Z`).getTime() >= cutoff) continue;
		try {
			fs.unlinkSync(path.join(dir, entry));
		} catch {
			// best effort — a locked or already-removed file is fine
		}
	}
}

export function writeAuditRecord(
	record: AuditRecord,
	env: NodeJS.ProcessEnv = process.env,
): void {
	try {
		const dir = auditLogDir(env);
		fs.mkdirSync(dir, { recursive: true, mode: 0o700 });
		const day = record.ts.slice(0, 10);
		fs.appendFileSync(
			path.join(dir, `audit-${day}.jsonl`),
			`${JSON.stringify(record)}\n`,
			{ mode: 0o600 },
		);
	} catch {
		// never let audit logging break the command
	}
}

let recordedArgv: string[] = [];

/** Re-points the record at the argv actually being served — the entrypoint
 * rewrites the raw argv (empty run → quickstart, prompts fill in missing
 * params) after `initAuditLog` has already captured it. */
export function setAuditArgv(argv: string[]): void {
	recordedArgv = redactArgv(argv);
}

export function initAuditLog(
	argv: string[],
	version: string,
	env: NodeJS.ProcessEnv = process.env,
): void {
	pruneAuditLogs(env);
	if (auditLogDisabled(env)) return;
	const startedAt = new Date();
	const start = performance.now();
	recordedArgv = redactArgv(argv);
	// A signal with no listener kills the process without firing "exit", which
	// would drop interrupted runs from the log — the ones most worth auditing.
	// These handlers preserve the conventional 128+signal exit codes, but
	// defer whenever any other listener exists so they can never pre-empt
	// someone else's signal handling.
	const exitOnSignal = (signal: NodeJS.Signals, code: number) => {
		process.on(signal, () => {
			if (process.listenerCount(signal) === 1) process.exit(code);
		});
	};
	exitOnSignal("SIGINT", 130);
	exitOnSignal("SIGTERM", 143);
	process.on("exit", (code) => {
		writeAuditRecord(
			{
				ts: startedAt.toISOString(),
				argv: recordedArgv,
				exitCode: code,
				durationMs: Math.round(performance.now() - start),
				version,
			},
			env,
		);
	});
}
